import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  ConfirmDialogComponent,
  ConfirmDialogInput,
} from '@qtek/libs/dialog-routes';
import { AiChatService } from '@qtek/libs/store';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { AlloPreferences, Message } from '@qtek/shared/models';
import { EMPTY, catchError, debounceTime, filter, switchMap, take } from 'rxjs';
import { AlloMemoryNoticeService } from '../../services/allo-memory-notice.service';

/**
 * One bot turn folds in one preference per extracted fact, so a single exchange can push several
 * `chatPref` frames. Coalesce them into one re-read.
 */
const REFRESH_DEBOUNCE_MS = 300;

/**
 * SD-2096 — the Allo settings panel: cross-session memory controls for the signed-in user.
 *
 * Shows what Allo has inferred about them, lets them clear it, and lets them opt out of the
 * feature entirely. Opting out stops both extraction and injection but deliberately keeps the
 * stored summary, so opting back in restores it rather than starting from nothing — clearing is
 * the separate, explicit action for forgetting.
 *
 * Follows {@link AiChatHistoryComponent}: one implementation shared by the two panels that host
 * Allo (the inline right-sidenav and the popped-out window), talking to the BE directly through
 * {@link AiChatService} rather than through the store, since none of this state is needed
 * anywhere else in the app.
 */
@Component({
  selector: 'qt-ai-chat-settings',
  standalone: true,
  imports: [
    MatIconModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatTooltipModule,
    TranslationCoreModule,
  ],
  templateUrl: './ai-chat-settings.component.html',
  styleUrls: ['./ai-chat-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatSettingsComponent {
  private readonly chatSvc = inject(AiChatService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly notices = inject(AlloMemoryNoticeService);

  protected readonly preferences = signal<AlloPreferences | null>(null);
  protected readonly loading = signal(true);
  /** Set while a toggle or clear is in flight, so the controls cannot be double-submitted. */
  protected readonly saving = signal(false);
  protected readonly error = signal<string | null>(null);

  protected readonly memoryOptOut = computed(
    () => this.preferences()?.memoryOptOut ?? false
  );

  /** The stored bullet list, or null when Allo has inferred nothing yet. */
  protected readonly summary = computed(() => {
    const stored = this.preferences()?.summary?.trim();
    return stored ? stored : null;
  });

  constructor() {
    this.load();
    this.watchForChanges();

    // Being on screen is what "the user has seen it" means, so the panel itself is what tells
    // the notice service whether a change needs announcing.
    this.notices.panelOpened();
    this.destroyRef.onDestroy(() => this.notices.panelClosed());
  }

  /**
   * SD-2096: keep an open panel in step with preferences changed elsewhere — above all from the
   * chat thread itself, where the user can ask Allo to remember or drop something. That fold-in
   * happens asynchronously on the BE after the bot turn, so the panel has no moment of its own at
   * which to re-read and would otherwise keep showing what it loaded when it opened. It also
   * covers the same user's other tab or popped-out window.
   *
   * The pushed frame carries no preference content (see `AiChatService.chatPrefChanged$`), so we
   * re-read through the same endpoint {@link load} uses.
   */
  private watchForChanges(): void {
    this.chatSvc.chatPrefChanged$
      .pipe(
        debounceTime(REFRESH_DEBOUNCE_MS),
        // Our own toggle/clear writes push a frame back too. Their responses already carry the
        // persisted state, so skip those rather than race a refetch against the write in flight.
        filter(() => !this.saving()),
        // Refresh silently: no spinner and no error banner, since the user did not ask for this
        // read — on failure the panel keeps showing what it already has until the next frame.
        switchMap(() =>
          this.chatSvc.getChatPreferences().pipe(catchError(() => EMPTY))
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(response => {
        this.preferences.set(response?.res ?? null);
        this.error.set(null);
      });
  }

  /**
   * (Re)load the panel. Hosts call this when they open the panel so a summary inferred since the
   * last look shows up without a page reload.
   */
  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.chatSvc
      .getChatPreferences()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.preferences.set(response?.res ?? null);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.error.set('GNR_ALLO_MEMORY_LOAD_FAILED');
        },
      });
  }

  protected toggleOptOut(optOut: boolean): void {
    if (this.saving()) return;
    this.saving.set(true);
    this.error.set(null);
    this.chatSvc
      .setChatMemoryOptOut(optOut)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.preferences.set(response?.res ?? null);
          this.saving.set(false);
        },
        error: () => {
          this.saving.set(false);
          // The toggle is bound to the stored state, so a failed write leaves the switch
          // showing what the BE actually has rather than the flip the user attempted.
          this.error.set('GNR_ALLO_MEMORY_SAVE_FAILED');
        },
      });
  }

  /**
   * Confirm, then clear. This is not recoverable from the UI — the BE keeps no history of what
   * was forgotten — so it goes behind the same destructive confirm dialog the history list uses.
   */
  protected confirmClear(): void {
    if (this.saving() || !this.summary()) return;

    const data: ConfirmDialogInput = {
      title: 'GNR_ALLO_MEMORY_CLEAR_TTL',
      message: new Message('GNR_ALLO_MEMORY_CLEAR_MSG'),
      acceptButton: 'GNR_ALLO_MEMORY_CLEAR',
      cancelButton: 'GNR_CANCEL',
      highlightTitle: true,
    };

    this.dialog
      .open(ConfirmDialogComponent, { data, autoFocus: false })
      .afterClosed()
      .pipe(take(1), filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.clear());
  }

  private clear(): void {
    this.saving.set(true);
    this.error.set(null);
    this.chatSvc
      .clearChatPreferences()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.preferences.set(response?.res ?? null);
          this.saving.set(false);
        },
        error: () => {
          this.saving.set(false);
          this.error.set('GNR_ALLO_MEMORY_CLEAR_FAILED');
        },
      });
  }
}
